import React from 'react';
import { logger, LogCategory } from '../../utils/logger';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; errorInfo?: React.ErrorInfo }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    logger.error(LogCategory.ERROR, 'React Error Boundary caught an error', error, 'ErrorBoundary');
    logger.error(LogCategory.ERROR, 'Error Info', errorInfo, 'ErrorBoundary');

    // In production, send to error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Log detailed error info for Railway logs
      logger.error(LogCategory.ERROR, 'Production Error Details', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      }, 'ErrorBoundary');
      
      // You can also send to external error tracking service here
      // Example: Sentry, LogRocket, Bugsnag, etc.
    }
    
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <this.props.fallback error={this.state.error!} errorInfo={this.state.errorInfo} />;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Application Error</h1>
              <p className="text-gray-600 mb-4">
                Something went wrong. Please try refreshing the page.
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                    Error Details (Development Only)
                  </summary>
                  <div className="bg-gray-100 p-3 rounded text-xs font-mono text-red-600 whitespace-pre-wrap">
                    <strong>Error:</strong> {this.state.error.message}
                    <br />
                    <br />
                    <strong>Stack:</strong>
                    <br />
                    {this.state.error.stack}
                  </div>
                </details>
              )}
              
              <button
                onClick={() => window.location.reload()}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}